import { Box, Typography, Stack } from '@mui/material';
import { FC, useState } from 'react';
import { Footer } from '@/components/details/Footer';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import BasicActivityInfo from '@/components/details/BasicActivityInfo';
import { PrivilegeEnum, SampleActivityDto, UpdateSampleActivityRequest, StatusDto } from '@/api/ApiClient';
import { useBackendStore } from '@/stores/backendStore';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import InlineImages from '@/components/common/InlineImages';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useSampleActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { log } from '@/lib/utils/logger';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { updateSampleActivityWithPreconditionRetry } from '@/lib/tree/submitEntityUtils';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createSampleActivityBasicSaveValidation } from '@/lib/validation/saveValidationBuilders';

export const selectOptions: OptionKey[] = ['researcherOptions', 'facultyOptions', 'status'];

const InventoryDetails: FC = () => {
  const backend = useBackendStore.getState().getBackend();
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { resolveUser } = useUserResolution();
  const { setDataModified, updateDataPartial, updateData } = useTree();
  const { selectedNode } = useTreeSelection();
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();

  // Check if this is a readonly node type
  const isReadOnlyNode = selectedNode ? nodeIsReadOnlyForUi(selectedNode.type) : false;

  // Track manual edit mode state (when user clicks "Edit Details" button)
  // This allows entering edit mode even when there are no modifications yet
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);

  const activity = useSampleActivityData(selectedNode);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  // Form is editable if: user manually entered edit mode OR there are unsaved modifications
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  // Centralized date handling utility
  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? new Date(date.toDateString()) : undefined;
  };

  if (!activity || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Select a node to view details
        </Typography>
      </Box>
    );
  }

  if (!options || optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }

  const handleModification = (modified: Partial<SampleActivityDto>) => {
    if (!activity || !selectedNode) return;
    updateDataPartial(selectedNode.dataId, modified);
    setDataModified(selectedNode.dataId, true);
  };

  const handleSave = async () => {
    if (!activity?.id) return;
    const saveValidation = validateRequiredToSave(createSampleActivityBasicSaveValidation(activity));
    if (!saveValidation.isValid) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    try {
      const request: UpdateSampleActivityRequest = { ...activity };
      const preconditionDeps = { showSnackbar, showConfirmDialog };
      const updated = await updateSampleActivityWithPreconditionRetry(backend, activity.id, request, preconditionDeps);
      if (updated === null) return;
      updateData(selectedNode.dataId, updated);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after successful save
      showSnackbar('Inventory activity saved successfully', 'success');
    } catch (error) {
      log.error('[InventoryActivity] Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save inventory activity.', 'error');
    }
  };

  // Handler for Footer's setIsReadOnly callback
  // When value === false: user clicked "Edit Details" → enter edit mode
  // When value === true: user clicked "Cancel" → exit edit mode and clear modifications
  const setIsReadOnly = (value: boolean) => {
    setIsManuallyEditing(!value); // false = edit mode, true = read-only mode
    if (value && selectedNode) {
      // Exiting edit mode: clear any unsaved modifications
      setDataModified(selectedNode.dataId, false);
    }
  };

  const isSubmitted = activity?.status === StatusDto.Submitted;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <BasicActivityInfo
            activity={activity}
            isReadOnly={isReadOnly}
            isSubmitted={isSubmitted}
            handleModification={handleModification}
            convertDateForBackend={convertDateForBackend}
            hideVisibility={isReadOnlyNode}
          />
          {/* Image Section */}
          <InlineImages title="Sample Images" />

          {/* Description */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Description:
            </Typography>
            {isReadOnly || isSubmitted ? (
              activity?.desc ? (
                <Typography variant="body1">
                  <span dangerouslySetInnerHTML={{ __html: activity.desc }} />
                </Typography>
              ) : (
                <Typography variant="body1">N/A</Typography>
              )
            ) : (
              <RichTextEditor
                value={activity?.desc ?? ''}
                onChange={(content) => handleModification({ desc: content })}
              />
            )}
          </Box>
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        {isReadOnlyNode ? (
          <ReadOnlyFooter
            lastChangedDate={activity?.lastChangedDate}
            lastChangedBy={activity?.lastChangedByUser || 'Unknown'}
            status={activity?.status || 'Draft'}
          />
        ) : (
          <Footer
            lastChangedDate={activity?.lastChangedDate}
            lastChangedBy={resolveUser(activity?.lastChangedByUser || '')}
            status={activity?.status}
            isReadOnly={isReadOnly}
            setIsReadOnly={setIsReadOnly}
            onSave={handleSave}
            privilege={PrivilegeEnum.Sample}
            hideDelete={true}
          />
        )}
      </Box>
    </Box>
  );
};

export default InventoryDetails;
